// Studby — AI Chat Tutor. Conversational, remembers context, guides not spoils.
import { useState, useRef, useEffect } from "react";
import { Send, Loader2, Sprout } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/Markdown";
import { Paywall } from "@/components/Paywall";

const GREETING = {
  role: "assistant",
  content: "Hi! I'm your Studby tutor. Ask me anything and I'll help you understand it — not just hand over the answer. What are we working on today?",
};

export default function ChatTutor() {
  const { hasAccess } = useAuth();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  if (!hasAccess) return <Paywall />;

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] sm:h-[calc(100vh-8rem)]">
      <div className="mb-4">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">AI Tutor</h1>
        <p className="text-muted-foreground text-sm">Explains, guides, and remembers your chat.</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <span className="grid place-items-center w-8 h-8 rounded-full bg-primary text-primary-foreground mr-2 shrink-0 self-end">
                <Sprout size={16} aria-hidden="true" />
              </span>
            )}
            <div
              data-testid={`chat-message-${m.role}`}
              className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                m.role === "user"
                  ? "bg-primary text-primary-foreground rounded-br-md"
                  : "bg-card border border-border rounded-bl-md"
              }`}
            >
              {m.role === "assistant"
                ? <Markdown>{m.content}</Markdown>
                : <p className="text-[15px] whitespace-pre-wrap">{m.content}</p>}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start items-center gap-2 text-muted-foreground text-sm">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
              <Sprout size={16} aria-hidden="true" />
            </span>
            <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>

      {error && <p className="text-sm text-destructive mt-2" data-testid="chat-error">{error}</p>}

      {/* Input */}
      <form onSubmit={send} className="mt-3 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask your tutor anything…"
          data-testid="chat-input"
          className="flex-1 bg-card border border-border rounded-full px-5 py-3 text-[15px] outline-none focus:border-accent transition-colors"
        />
        <Button type="submit" disabled={loading || !input.trim()} className="rounded-full h-12 w-12 p-0 shrink-0" data-testid="chat-send-button">
          <Send size={18} aria-hidden="true" />
        </Button>
      </form>
    </div>
  );
}
